<template>
  <div
    class="transaction-card bg-color-dark rounded-md border border-solid border-color-dark w-full"
  >
    <div
      class="flex justify-between flex-col sm:flex-row sm:flex-wrap pb-[16px]"
    >
      <div
        class="h-[21px] flex items-center justify-between sm:justify-start w-full sm:w-auto box-border"
      >
        <div class="status h-[20px] flex items-center">
          <img
            class="w-[20px] h-[20px]"
            :src="isOutgoing ? ErrorIcon : ReceivedIcon"
          />
          <p class="ml-[8px] text-md status-text leading-[20px]">
            {{ t(isOutgoing ? "Sent" : "Received") }}
          </p>
        </div>
      </div>
      <div class="text-base sm:w-fit text-[color:var(--dark-400)] w-full">
        {{ item.timestamp }}
      </div>
    </div>

    <div
      class="flex justify-between items-center highlight-block"
      :class="statusState.highlightClass"
    >
      <div class="w-full sm:w-fit amount-display text-[20px]">
        <template v-if="hasAmountData">
          <span>
            {{ displaySign }}{{ formattedAmount }} {{ displaySymbol }}
          </span>
          <span v-if="formattedUsdValue" class="usd-value">
            {{ formattedUsdValue }}
          </span>
        </template>
        <template v-else>-</template>
      </div>
      <div v-if="!isTronTransfer" class="confirmation-block">
        <div class="flex sm:items-center gap-[8px] pt-4 sm:py-0 status-wrapper">
          <img :src="statusState.icon" />
          <div class="status-text" :class="statusState.class">
            {{ statusState.title }}
          </div>
        </div>
        <div v-if="item.confirmationCount" class="confirmation-text">
          {{ `${item.confirmationCount} ${t("Transaction | Confirmations")}` }}
        </div>
      </div>
    </div>

    <div class="bottom-block">
      <router-link
        v-if="item.network && item.hash"
        :to="{
          name: 'Transaction Details',
          params: { network: item.network, id: item.hash },
        }"
        class="transaction-hash text-base truncate md:text-clip md:w-auto transition-colors link"
      >
        <Ellipsis :text="item.hash || 'Unconfirmed transaction'" />
      </router-link>
      <div
        v-else
        class="mt-1 text-base truncate md:text-clip w-[200px] md:w-auto"
      >
        {{ item.hash || "Unconfirmed transaction" }}
      </div>

      <button
        class="flex flex-nowrap flex--center-center gap-1 copy-button"
        @click="copy(item.hash)"
      >
        <BaseSvgIcon name="copy" class="copy" />
        <span class="copy-button-text">
          {{ t("Transaction | Copy hash") }}
        </span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { getCurrentInstance, computed } from "vue";
import { useI18n } from "vue-i18n";
// Import formatters
import { amount as formatBalance } from "@/shared/utils/format";
import { copyToClipboard } from "@emcd/common/helpers";

import ReceivedIcon from "../assets/green-plus.svg";
import GreenCheckmarkIcon from "../assets/green-checkmark.svg";
import ErrorIcon from "../assets/red-minus.svg";
import RedCross from "../assets/red-cross.svg";
import WaitingIcon from "../assets/waiting.svg";
import { BaseSvgIcon, Ellipsis } from "@/shared/ui";
import { formatFiat } from "@/shared/utils/formaters/transactionFormatter";

const { t } = useI18n();

const props = defineProps({
  /**
   * @type item - transaction info
   * @property { string } - timestamp
   * @property { number } - confirmationCount
   * @property { boolean } - confirmed
   * @property { string } - hash
   * @property { string } - incoming/outgoing
   * @property { string } - network [btc/tron]
   */
  item: {
    type: Object,
    required: true,
  },
  isTronTransfer: {
    type: Boolean,
    default: false,
  },
});

const DIRECTION = {
  OUTGOING: "outgoing",
};

// Проверка направления транзакции
const isOutgoing = computed(() => props.item.direction === DIRECTION.OUTGOING);

// Placeholder for fiat formatting

const instanse = getCurrentInstance();
const { $toast } = instanse.appContext.config.globalProperties;

// --- New Computed Properties for Amount Display ---
// Check if we have valid amount data
const hasAmountData = computed(() => {
  return props.item && props.item.amountDetails;
});

// Format the main amount
const formattedAmount = computed(() => {
  if (!hasAmountData.value) return "";
  if (props.item.network === "ton" || props.item.network === "ethereum") {
    return props.item.amountDetails.amount;
  }
  return formatBalance(
    props.item.amountDetails.amount,
    props.item.amountDetails.decimals,
  );
});

// Format the USD value
const formattedUsdValue = computed(() => {
  if (
    !hasAmountData.value ||
    props.item.amountDetails.usd_value === null ||
    props.item.amountDetails.usd_value === undefined
  )
    return "";
  return formatFiat(props.item.amountDetails.usd_value);
});

// Get the symbol
const displaySymbol = computed(() => {
  if (props.item.network === "ton") {
    return props?.item?.amountDetails?.symbol?.toUpperCase() || "";
  }
  return hasAmountData.value ? props.item.amountDetails.symbol : "";
});

// Determine the sign prefix based on direction
const displaySign = computed(() => {
  if (!hasAmountData.value) return "";
  return props.item.direction === "outgoing" ? "− " : "+ ";
});

// --- End New Computed Properties ---

function copy(value) {
  copyToClipboard(value, () => "");
  $toast({
    type: "success",
    title: t("HashCopied"),
  });
}

const statusState = computed(() => {
  if (props.item.network === "ton" && props.item.confirmed) {
    return {
      title: t("Successful"),
      icon: GreenCheckmarkIcon,
      class: "status-text--success",
    };
  }

  if (props.item.network === "ton" && !props.item.confirmed) {
    return {
      title: "Error",
      icon: RedCross,
      class: "status-text--error",
      highlightClass: "highlight-block--error",
    };
  }

  const isWaiting = props.item.confirmationCount === 0 && !props.item.confirmed;
  if (isWaiting) {
    return {
      title: "Pending",
      icon: WaitingIcon,
      class: "status-text--pending",
      highlightClass: "highlight-block--pending",
    };
  }

  // Only show error if confirmations = 0 AND confirmed = true (or explicit error flag)
  if (
    props.item.error ||
    (props.item.confirmationCount === 0 && props.item.confirmed)
  ) {
    return {
      title: "Error",
      icon: RedCross,
      class: "status-text--error",
      highlightClass: "highlight-block--error",
    };
  }

  const isSuccess = props.item.confirmed && props.item.confirmationCount > 0;
  if (isSuccess) {
    return {
      title: t("Successful"),
      icon: GreenCheckmarkIcon,
      class: "status-text--success",
    };
  }

  return {
    title: t("Error"),
    icon: RedCross,
    class: "status-text--error",
    highlightClass: "highlight-block--error",
  };
});
</script>

<style scoped lang="scss">
/* Styles for amount classes */
.amount-display {
  font-size: 20px; // Adjust as needed
  line-height: 1.5;
  font-weight: 500;
  color: #fafafa; // Default color for the container and USD part

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    font-size: 12px;
  }
}

.status {
  @media (max-width: 768px) {
    height: 20px;
    margin-bottom: 4px;
  }
}

.transaction-card {
  padding: 32px;

  @media (max-width: 768px) {
    border-radius: 12px;
    padding: 24px;
  }
}

.transaction-hash {
  color: #a3a3a3;
  flex-grow: 1;

  &:hover {
    color: #8f42ff;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
}

.confirmation-text {
  font-size: 12px;
  color: var(--text-6);
  width: 100%;
  padding-bottom: 0;

  @media (max-width: 768px) {
    width: auto;
    padding-top: 10px;
    white-space: nowrap;
    font-size: 10px;
  }
}

.bottom-block {
  display: flex;
  justify-content: space-between;
  align-items: end;
  margin-top: 16px;

  @media (max-width: 768px) {
    margin-top: 16px;
  }
}

.status-text {
  font-size: 14px;

  &--error {
    color: #e53935;
  }

  &--success {
    color: #43a047;
  }

  &--pending {
    color: #f59e0b;
  }

  @media (max-width: 768px) {
    font-size: 12px;
  }
}

.usd-value {
  margin-left: 16px;
  color: #4d4d4d;

  @media (max-width: 768px) {
    margin-left: 0px;
  }
}

.highlight-block {
  border: 1px solid #2d2d2d;
  border-radius: 10px;
  padding: 15px 20px;
  display: flex;
  position: relative;

  @media (max-width: 768px) {
    padding: 16px;
  }

  &--pending,
  &--error {
    &::after {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      height: 100%;
      width: 20%;
      display: block;
      border: 1px solid #f59e0b;
      border-left: none;
      border-top-right-radius: 10px;
      border-bottom-right-radius: 10px;
      border-bottom-left-radius: 0;
      border-top-left-radius: 0;
      mask-image: linear-gradient(
        to right,
        rgba(255, 255, 255, 0),
        rgba(255, 255, 255, 1)
      );

      z-index: 2;
    }
  }

  &--error {
    &::after {
      border-color: #e53935;
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 15px;
  }
}

.copy-button {
  @media (max-width: 768px) {
    height: 100%;
  }

  &:hover {
    .copy-button-text {
      transition: all 0.3s ease;
      color: #8f42ff;
    }
    .copy {
      transition: all 0.3s ease;
      filter: brightness(0) saturate(100%) invert(33%) sepia(39%)
        saturate(7499%) hue-rotate(252deg) brightness(101%) contrast(101%);
    }
  }
}

.copy {
  filter: brightness(0) saturate(100%) invert(73%) sepia(25%) saturate(7298%)
    hue-rotate(217deg) brightness(100%) contrast(99%);

  @media (max-width: 768px) {
    width: 16px;
    height: 16px;
  }
}

.copy-button-text {
  color: #c18afd;
  font-size: 14px;
  font-weight: 500;

  @media (max-width: 768px) {
    display: none;
  }
}

.confirmation-block {
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: row-reverse;
    justify-content: space-between;
  }
}

.status-wrapper {
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;

  @media (max-width: 768px) {
    margin-left: 4px;
    gap: 4px;
    padding-top: 10px;
    padding-bottom: 0;
    margin-right: 15px;
    align-items: center;
  }
}
</style>
